import { ScrollArea, Stack, Textarea } from '@mantine/core';
import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import AppIcon from '@/components/AppIcon.tsx';
import { HiOutlineArrowUpCircle, HiStopCircle } from 'react-icons/hi2';
import useAddChatMessage from '@/hooks/api/runs/chat/useAddChatMessage.ts';
import useRunId from '@/hooks/useRunId.ts';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import classes from './ResultsChat.module.css';
import useRetrieveRunChatMessages from '@/hooks/api/runs/chat/useRetrieveRunChatMessages.ts';
import CustomSpinner from '@/components/Spinner.tsx';
import ChatMessageRenderer from '@/layouts/ResultsChat/ChatMessageRenderer.tsx';
import useCancelChatMessage from '@/hooks/api/runs/chat/useCancelChatMessage.ts';
import { useScrollIntoView } from '@mantine/hooks';
import { ChatMessage, ChatMessageStatus } from '@/types/runs.ts';

const STARTING_MESSAGE: ChatMessage = {
  id: 'starting',
  role: 'assistant',
  content:
    'Hey there! I can help you answer questions about the results or dive deeper into how something was calculated.',

  created_by: null,
  created_by_type: 'user',
  created_by_display_name: '',
  created_by_email: '',
  created_by_label: 'Unknown creator',
  created_at: new Date().toISOString(),
  status: ChatMessageStatus.Completed,
  active: false,
};

const ResultsChat = () => {
  const runId = useRunId();

  const { data: chatMessages, isPending: loadingMessages } = useRetrieveRunChatMessages();

  const [newMessage, setNewMessage] = useState<string>('');

  const { mutate: addChatMessage, isPending: addingChatMessage } = useAddChatMessage();
  const { mutate: cancelChatMessage, isPending: cancellingChatMessage } = useCancelChatMessage();

  const activeMessage = useMemo(
    () => chatMessages?.results?.find((message) => message.active),
    [chatMessages?.results],
  );

  const allMessages = useMemo(() => {
    return [STARTING_MESSAGE, ...(chatMessages?.results || [])];
  }, [chatMessages?.results]);

  const addMessage = () => {
    addChatMessage({ run_id: runId, content: newMessage });
    setNewMessage('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // Optional: prevent newline
      addMessage();
    }
  };

  const { scrollIntoView, targetRef, scrollableRef } = useScrollIntoView<HTMLDivElement>({
    offset: 0,
  });

  useEffect(() => {
    if (allMessages) {
      scrollIntoView();
    }
  }, [allMessages, scrollIntoView]);

  const buttonActive = !!activeMessage || !!newMessage;
  const isPending = addingChatMessage || cancellingChatMessage;

  return (
    <Stack gap={0} h={'calc(100% - 50px)'} mt={'-md'} pos={'relative'}>
      <ScrollArea w={'100%'} h={'100%'} viewportRef={scrollableRef}>
        <Stack px={'sm'} pt={'sm'} gap={'sm'}>
          {loadingMessages ? (
            <CustomSpinner />
          ) : (
            <>
              {allMessages.map((message, index) => (
                <ChatMessageRenderer message={message} index={index} key={message.id} />
              ))}
            </>
          )}
        </Stack>
        <div ref={targetRef}></div>
      </ScrollArea>
      <Stack w={'100%'} px={'xs'} mb={'lg'} mt={0} pos={'relative'}>
        {/*<CustomLoadingOverlay visible={isPending} />*/}
        <Textarea
          placeholder={'Ask a question...'}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          radius={'xl'}
          onKeyDown={handleKeyDown}
          size="sm"
          autosize={true}
          minRows={1}
          maxRows={9}
          classNames={{ input: classes.focusedTextarea }}
          rightSection={
            <StyledButton
              variant={'subtleIcon'}
              size={'sm'}
              aria-label={activeMessage ? 'Stop response' : 'Send message'}
              style={{
                height: '100%',
                width: '34px',
                marginRight: '12px',
              }}
              disabled={!buttonActive}
              loading={isPending}
              onClick={() => (activeMessage ? cancelChatMessage({ run_id: runId }) : addMessage())}
            >
              <AppIcon
                Icon={activeMessage ? HiStopCircle : HiOutlineArrowUpCircle}
                size={26}
                iconProps={{ color: 'var(--velos-brand-blue)' }}
                hoverIconProps={{ color: '#5A61FC' }}
              />
            </StyledButton>
          }
          disabled={!!activeMessage}
        />
      </Stack>
    </Stack>
  );
};

export default ResultsChat;
